import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { classnames } from 'helpers/utils';
import styles from './bottom-sheet.module.scss';

interface BottomSheetProps {
  open: boolean;
  onDismiss?: () => void;
  className?: string;
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  scrollLocking?: boolean;
  skipInitialTransition?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onDismiss,
  className,
  children,
  header,
  footer,
  scrollLocking = true,
  skipInitialTransition = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);

      if (skipInitialTransition) {
        setIsAnimating(true);
      } else {
        setTimeout(() => setIsAnimating(true), 10);
      }

      if (scrollLocking) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      setIsAnimating(false);
      setTimeout(() => {
        setIsVisible(false);
        if (scrollLocking) {
          document.body.style.overflow = '';
        }
      }, 300);
    }
  }, [open, scrollLocking, skipInitialTransition]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open && onDismiss) {
        event.preventDefault();
        onDismiss();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }

    return undefined;
  }, [open, onDismiss]);

  if (!isVisible) return null;

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && onDismiss) {
      onDismiss();
    }
  };

  const handleOverlayKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      event.target === event.currentTarget &&
      onDismiss
    ) {
      event.preventDefault();
      onDismiss();
    }
  };

  const sheetContent = (
    <div
      className={classnames(styles.backdrop, {
        [styles.backdropVisible]: isAnimating,
      })}
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Close dialog"
    >
      <div
        className={classnames(
          styles.bottomSheet,
          {
            [styles.bottomSheetVisible]: isAnimating,
          },
          className || '',
        )}
        role="dialog"
        aria-modal="true"
      >
        {header && <div className={styles.header}>{header}</div>}
        <div className={styles.content}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );

  return createPortal(sheetContent, document.body);
};

export { BottomSheet };
